import { Drawer } from 'vaul';

export function BottomSheet({ 
  trigger, 
  title, 
  description, 
  children, 
  snapPoints = [0.4, 1], // Opens to 40% height, can pull to 100%
  outsideClose = true 
}) {
  return (
    <Drawer.Root shouldScaleBackground snapPoints={snapPoints}>
      {/* asChild allows you to pass your own button/element as a trigger */}
      <Drawer.Trigger asChild>
        {trigger}
      </Drawer.Trigger>
      
      <Drawer.Portal>
        {/* The dimmed background */}
        <Drawer.Overlay className="fixed inset-0 bg-[#00000066] z-50" />
        
        <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 mt-24 flex h-full max-h-[96%] flex-col rounded-t-[10px] bg-White">
          {/* The accessible "Drag Handle" bar */}
          <div className="mx-auto mt-4 h-1.5 w-12 flex-shrink-0 rounded-full bg-[#D4D4D8]" />
          
          <div className="flex-1 overflow-y-auto rounded-t-[10px] bg-white p-4">
            <div className="mx-auto max-w-md">
              {title && (
                <Drawer.Title className="text-lg font-semibold text-zinc-900">
                  {title}
                </Drawer.Title>
              )}
              {description && (
                <Drawer.Description className="text-sm text-zinc-600 mb-4">
                  {description}
                </Drawer.Description>
              )}
              
              {/* This is where your custom content renders */}
              <div className="mt-2">
                {children}
              </div>
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

/**
 * Controlled BottomSheet - open/close controlled via props
 * Use this when you need programmatic control over the sheet
 */
export function ControlledBottomSheet({ 
  open,
  onOpenChange,
  title, 
  description, 
  children, 
  dismissible = true,
  showHandle = true,
  showCloseButton = false,
  onClose,
  className = '',
  // snapPoints = [0.4, 0.7, 1], // 40%, 70%, 100% for mobile
}) {
  const handleOpenChange = (isOpen) => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
    if (!isOpen && onClose) {
      onClose();
    }
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={handleOpenChange}
      dismissible={dismissible}
      repositionInputs={false}
      // snapPoints={snapPoints}
    >
      <Drawer.Portal>
        {/* Overlay */}
        <Drawer.Overlay className="fixed inset-0 bg-[#00000066] z-50" />
        {/* Content */}
        <Drawer.Content className={`fixed bottom-0 left-0 right-0 z-50 mt-24 flex flex-col rounded-t-[10px] bg-White max-h-[80vh] ${className}`}>
          {/* Drag handle */}
          {showHandle && (
            <div className="mx-auto mt-4 h-1.5 w-12 flex-shrink-0 rounded-full bg-[#D4D4D8]" />
          )}
          {/* Close button */}
          {showCloseButton && (
            <button
              onClick={() => handleOpenChange(false)}
              disabled={!dismissible}
              className="absolute right-4 top-4 z-10 p-1 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          {/* Header */}
          {(title || description) && (
            <div className="px-4 pt-2 pb-0">
              {title && (
                <Drawer.Title className="text-lg font-semibold text-zinc-900">
                  {title}
                </Drawer.Title>
              )}
              {description && (
                <Drawer.Description className="text-sm text-zinc-600">
                  {description}
                </Drawer.Description>
              )}
            </div>
          )}
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto overscroll-contain pb-32">
            {children}
          </div>
          {/* Extra bottom padding for keyboard */}
          <div className="h-8 md:h-safe-bottom bg-white" />
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}